import {
  deepClone,
  isPermissionObject,
  mergeOverviewDashboardWidgetsIntoSms,
  normalizeAbacOverviewModule,
  syncMergedDashboardWidgetsToOverview,
} from "./abacPermissionUtils";
import { resolveAbacApiRole } from "./abacRoleTemplates";

/** Channel keys as stored under API `permissions.channels`. */
const API_CHANNEL_KEYS = [
  "sms",
  "whatsapp",
  "telegram",
  "email",
  "instagram",
  "messenger",
  "rcs",
  "push_notification",
];

const UI_TO_API_CHANNEL = {
  push: "push_notification",
  pushNotification: "push_notification",
  push_notification: "push_notification",
};

const META_KEYS = new Set(["role", "_description", "description"]);

/**
 * Product UI lists Subscription under SMS Utilities. Backend JSON keeps it at
 * channels.sms.subscription. Editor shows it nested; save writes the API path.
 */
function nestSmsSubscriptionUnderUtilities(tree) {
  if (!isPermissionObject(tree?.sms)) return tree;
  const sms = tree.sms;
  if (!isPermissionObject(sms.subscription)) return tree;
  sms.sms_utilities = isPermissionObject(sms.sms_utilities)
    ? sms.sms_utilities
    : {};
  if (!isPermissionObject(sms.sms_utilities.subscription)) {
    sms.sms_utilities.subscription = sms.subscription;
  }
  delete sms.subscription;
  return tree;
}

function liftSmsSubscriptionOutOfUtilities(tree) {
  if (!isPermissionObject(tree?.sms)) return tree;
  const sms = tree.sms;
  const nested = sms.sms_utilities?.subscription;
  if (!isPermissionObject(nested)) return tree;
  if (!isPermissionObject(sms.subscription)) {
    sms.subscription = nested;
  }
  if (isPermissionObject(sms.sms_utilities)) {
    delete sms.sms_utilities.subscription;
  }
  return tree;
}

/**
 * Align company/vendor leaf keys so UI checks (edit/view) match API (update/list).
 * Company API JSON has `edit` only — never invent `update`.
 */
function normalizeAdminModuleLeafAliases(moduleNode, options = {}) {
  const { syncEditUpdate = true } = options;
  if (!isPermissionObject(moduleNode)) return moduleNode;
  const next = { ...moduleNode };
  if (syncEditUpdate) {
    if (typeof next.edit !== "boolean" && typeof next.update === "boolean") {
      next.edit = next.update;
    }
    if (typeof next.update !== "boolean" && typeof next.edit === "boolean") {
      next.update = next.edit;
    }
  }
  if (typeof next.view !== "boolean" && typeof next.list === "boolean") {
    next.view = next.list;
  }
  // Balance leaves: API may use camelCase / alternate snake_case.
  if (
    typeof next.balance_add !== "boolean" &&
    typeof next.add_balance === "boolean"
  ) {
    next.balance_add = next.add_balance;
  } else if (
    typeof next.balance_add !== "boolean" &&
    typeof next.balanceAdd === "boolean"
  ) {
    next.balance_add = next.balanceAdd;
  }
  if (
    typeof next.balance_deduct !== "boolean" &&
    typeof next.deduct_balance === "boolean"
  ) {
    next.balance_deduct = next.deduct_balance;
  } else if (
    typeof next.balance_deduct !== "boolean" &&
    typeof next.balanceDeduct === "boolean"
  ) {
    next.balance_deduct = next.balanceDeduct;
  }
  return next;
}

function toPositiveId(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeAccessName(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

/** Access name used at multi-access login (e.g. "Nikhil Mehra"). */
export function resolveAbacAccessName(user) {
  if (!user || typeof user !== "object") return "";
  const candidates = [
    user.accessName,
    user.accessname,
    user.isMultiAccessMember ? user.systemId : null,
    user.isMultiAccessMember ? user.username : null,
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (s) return s;
  }
  return "";
}

/** Parent account id for GET /multiUser/list/access?userId= */
export function resolveAbacListUserId(user) {
  if (!user || typeof user !== "object") return null;
  return (
    toPositiveId(user.parentUserId) ||
    toPositiveId(user.multiUserId) ||
    toPositiveId(user.userId) ||
    toPositiveId(user.userid) ||
    toPositiveId(user.id)
  );
}

/** Rows from GET /multiUser/list/access — paged `data[]` or legacy `members[]`. */
export function getAccessListRows(accessPayload) {
  if (Array.isArray(accessPayload?.data)) return accessPayload.data;
  const root = accessPayload?.data ?? accessPayload ?? {};
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.members)) return root.members;
  if (Array.isArray(root.accesses)) return root.accesses;
  return [];
}

/**
 * Pick member.id from /multiUser/list/access response by accessName.
 * Example: data[{ id: 47, accessName: "Nikhil Mehra", userId: 404 }] → 47
 */
export function matchAccessMemberId(accessPayload, accessNameHint) {
  const members = getAccessListRows(accessPayload);
  if (!members.length) return null;

  const hint = normalizeAccessName(accessNameHint);
  if (hint) {
    const match = members.find(
      (m) =>
        normalizeAccessName(m?.accessName) === hint ||
        normalizeAccessName(m?.accessname) === hint ||
        normalizeAccessName(m?.systemId) === hint
    );
    if (match) return toPositiveId(match.id);
  }

  return null;
}

/**
 * Numeric multi-access row id for GET /multiUser/permissions/{id}.
 * Never uses parent account id (JWT user.id).
 */
export function resolveAbacAccessId(userOrId) {
  if (userOrId === null || userOrId === undefined || userOrId === "") {
    return null;
  }
  if (typeof userOrId === "number" || typeof userOrId === "string") {
    return toPositiveId(userOrId);
  }

  const candidates = [
    userOrId.accessId,
    userOrId.multiUserEntryId,
    userOrId.accesses?.[0]?.id,
  ];

  for (const c of candidates) {
    const n = toPositiveId(c);
    if (n) return n;
  }

  const parentId = toPositiveId(userOrId.parentUserId);
  const selfId = toPositiveId(userOrId.id);
  if (
    userOrId.isMultiAccessMember &&
    parentId &&
    selfId &&
    parentId !== selfId
  ) {
    return selfId;
  }

  return null;
}

/** Parent user id for PUT body `userid`. */
export function resolveAbacParentUserId(user) {
  if (!user || typeof user === "string") return null;

  const candidates = [
    user.parentUserId,
    user.userid,
    user.userId,
    !user.isMultiAccessMember ? user.id : null,
  ];

  for (const c of candidates) {
    if (c === null || c === undefined || c === "") continue;
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

/**
 * Flatten API permissions for the ABAC editor tabs.
 * API: { common, overview, channels: { sms, whatsapp, ... }, user, ... }
 * UI:  { default, navbar, overview, sms, whatsapp, ..., user, ... }
 */
export function flattenAbacPermissionsForUi(permissions) {
  if (!isPermissionObject(permissions)) return {};

  const src = deepClone(permissions);
  const out = {};

  if (src.role) out.role = src.role;
  if (src._description) out._description = src._description;

  if (isPermissionObject(src.common)) {
    if (isPermissionObject(src.common.default)) out.default = src.common.default;
    if (isPermissionObject(src.common.navbar)) out.navbar = src.common.navbar;
    if (isPermissionObject(src.common.wallet)) {
      out.navbar = isPermissionObject(out.navbar) ? out.navbar : {};
      out.navbar.wallet = src.common.wallet;
    }
  } else {
    if (isPermissionObject(src.default)) out.default = src.default;
    if (isPermissionObject(src.navbar)) out.navbar = src.navbar;
  }

  if (isPermissionObject(src.overview)) out.overview = src.overview;

  if (isPermissionObject(src.channels)) {
    for (const key of Object.keys(src.channels)) {
      const value = src.channels[key];
      if (!isPermissionObject(value)) continue;
      if (API_CHANNEL_KEYS.includes(key) || key === "push") {
        out[key === "push_notification" ? "push" : key] = value;
      } else {
        out.channels = out.channels || {};
        out.channels[key] = value;
      }
    }
  } else {
    for (const key of [
      "sms",
      "whatsapp",
      "telegram",
      "email",
      "instagram",
      "messenger",
      "rcs",
      "push",
      "push_notification",
    ]) {
      if (isPermissionObject(src[key])) {
        out[key === "push_notification" ? "push" : key] = src[key];
      }
    }
  }

  const passthrough = [
    "user",
    "sales_team",
    "company",
    "vendor",
    "address_book",
    "platform_documents",
    "administration",
    "other",
    "platform",
    "billing",
    "dlt",
    "network",
    "compliance",
    "pcap",
    "api_runner",
    "tier",
    "pricing",
  ];
  for (const key of passthrough) {
    if (isPermissionObject(src[key])) out[key] = src[key];
  }

  if (isPermissionObject(src.administration)) {
    const admin = src.administration;
    if (!out.user && isPermissionObject(admin.users)) out.user = admin.users;
    if (!out.sales_team && isPermissionObject(admin.salesTeam)) {
      out.sales_team = admin.salesTeam;
    }
    if (!out.company && isPermissionObject(admin.companies)) {
      out.company = admin.companies;
    }
    if (!out.vendor && isPermissionObject(admin.vendors)) {
      out.vendor = admin.vendors;
    }
    if (!out.address_book && isPermissionObject(admin.addressBook)) {
      out.address_book = admin.addressBook;
    }
  }

  if (
    !out.platform_documents &&
    isPermissionObject(src.other?.platform_documents)
  ) {
    out.platform_documents = src.other.platform_documents;
  }

  if (isPermissionObject(out.company)) {
    out.company = normalizeAdminModuleLeafAliases(out.company, {
      syncEditUpdate: false,
    });
    delete out.company.update;
  }
  if (isPermissionObject(out.vendor)) {
    out.vendor = normalizeAdminModuleLeafAliases(out.vendor);
  }

  return mergeOverviewDashboardWidgetsIntoSms(
    nestSmsSubscriptionUnderUtilities(normalizeAbacOverviewModule(out))
  );
}

/**
 * Nest UI permissions into the update-permissions API body shape.
 * PUT /multiUser/update-permissions → accesses[].permissions
 */
export function nestAbacPermissionsForApi(permissions) {
  if (!isPermissionObject(permissions)) return {};

  const src = syncMergedDashboardWidgetsToOverview(
    liftSmsSubscriptionOutOfUtilities(
      deepClone(normalizeAbacOverviewModule(permissions) || {})
    )
  );
  META_KEYS.forEach((k) => delete src[k]);

  const common = isPermissionObject(src.common) ? { ...src.common } : {};
  if (!isPermissionObject(common.default) && isPermissionObject(src.default)) {
    common.default = src.default;
  }
  if (!isPermissionObject(common.navbar) && isPermissionObject(src.navbar)) {
    const navbar = { ...src.navbar };
    if (isPermissionObject(navbar.wallet)) {
      if (!isPermissionObject(common.wallet)) common.wallet = navbar.wallet;
      delete navbar.wallet;
    }
    common.navbar = navbar;
  }
  if (!isPermissionObject(common.wallet) && isPermissionObject(src.wallet)) {
    common.wallet = src.wallet;
  }

  const channels = isPermissionObject(src.channels) ? { ...src.channels } : {};
  for (const uiKey of [
    "sms",
    "whatsapp",
    "telegram",
    "email",
    "instagram",
    "messenger",
    "rcs",
    "push",
    "push_notification",
  ]) {
    if (!isPermissionObject(src[uiKey])) continue;
    const apiKey = UI_TO_API_CHANNEL[uiKey] || uiKey;
    if (!isPermissionObject(channels[apiKey])) channels[apiKey] = src[uiKey];
  }

  // Keep only real channel modules under channels (drop list/management leftovers).
  const cleanChannels = {};
  for (const [key, value] of Object.entries(channels)) {
    if (!isPermissionObject(value)) continue;
    if (API_CHANNEL_KEYS.includes(key) || key === "push") {
      const apiKey = UI_TO_API_CHANNEL[key] || key;
      cleanChannels[apiKey] = value;
    }
  }

  const api = {};
  if (Object.keys(common).length) api.common = common;
  if (isPermissionObject(src.overview)) api.overview = src.overview;
  if (Object.keys(cleanChannels).length) api.channels = cleanChannels;

  if (isPermissionObject(src.user)) api.user = src.user;
  else if (isPermissionObject(src.administration?.users)) {
    api.user = src.administration.users;
  }

  if (isPermissionObject(src.sales_team)) api.sales_team = src.sales_team;
  else if (isPermissionObject(src.administration?.salesTeam)) {
    api.sales_team = src.administration.salesTeam;
  }

  if (isPermissionObject(src.company)) {
    api.company = normalizeAdminModuleLeafAliases(src.company, {
      syncEditUpdate: false,
    });
  } else if (isPermissionObject(src.administration?.companies)) {
    api.company = normalizeAdminModuleLeafAliases(src.administration.companies, {
      syncEditUpdate: false,
    });
  }
  if (isPermissionObject(api.company)) {
    delete api.company.update;
  }

  if (isPermissionObject(src.vendor)) {
    api.vendor = normalizeAdminModuleLeafAliases(src.vendor);
  } else if (isPermissionObject(src.administration?.vendors)) {
    api.vendor = normalizeAdminModuleLeafAliases(src.administration.vendors);
  }

  if (isPermissionObject(src.address_book)) api.address_book = src.address_book;
  else if (isPermissionObject(src.administration?.addressBook)) {
    api.address_book = src.administration.addressBook;
  }

  if (isPermissionObject(src.platform_documents)) {
    api.platform_documents = src.platform_documents;
  } else if (isPermissionObject(src.other?.platform_documents)) {
    api.platform_documents = src.other.platform_documents;
  }

  return api;
}

/** Normalize GET payloads into a flat permissions tree for the editor. */
export function extractPermissionsFromResponse(payload, accessId) {
  if (!payload || typeof payload !== "object") return {};

  const root = payload.data ?? payload;
  let permissions = null;

  if (isPermissionObject(root.permissions)) {
    permissions = root.permissions;
  } else if (Array.isArray(root.accesses) && root.accesses.length) {
    const match =
      root.accesses.find(
        (a) => Number(a?.id) === Number(accessId) && a?.permissions
      ) || root.accesses.find((a) => a?.permissions);
    if (match?.permissions) permissions = match.permissions;
  } else if (
    isPermissionObject(root) &&
    (root.overview ||
      root.sms ||
      root.whatsapp ||
      root.navbar ||
      root.common ||
      root.channels ||
      root.administration ||
      root.user)
  ) {
    permissions = root;
  }

  if (!permissions) return {};
  return flattenAbacPermissionsForUi(permissions);
}

/** Build PUT /multiUser/update-permissions body from UI state + target user. */
export function buildUpdatePermissionsBody(user, permissions) {
  const accessId = resolveAbacAccessId(user);
  const userid = resolveAbacParentUserId(user);
  const permissionsBody = nestAbacPermissionsForApi(permissions);

  return {
    userid: Number(userid),
    accesses: [
      {
        id: Number(accessId),
        permissions: permissionsBody,
      },
    ],
  };
}

export { resolveAbacApiRole };
